import axiosInstance from '../libs/axiosInstance';

export const aiQuizService = {
  getUsage: async () => {
    const response = await axiosInstance.get('/ai-quiz/usage/');
    return response.data;
  },

  getQuizReview: async (sessionId) => {
    const response = await axiosInstance.get(`/ai-quiz/review/${sessionId}/`);
    return response.data;
  },
  /**
   * Retrieves a list of all study materials uploaded by the current student.
   * GET /api/ai-quiz/materials/
   */
  getStudyMaterials: async () => {
    const response = await axiosInstance.get(`/ai-quiz/materials/`);
    return response.data;
  },

  /**
   * Fetch status and extracted topics for a single material.
   * GET /api/ai-quiz/materials/<id>/
   */
  getStudyMaterialDetail: async (id) => {
    const response = await axiosInstance.get(`/ai-quiz/materials/${id}/`);
    return response.data;
  },

  /**
   * Upload a new study document (.pdf, .docx, .pptx, .txt - Max 50MB)
   * or register a YouTube URL.
   * POST /api/ai-quiz/materials/
   */
  uploadStudyMaterial: async (data, onUploadProgress) => {
    const isFormData = data instanceof FormData;
    const response = await axiosInstance.post(`/ai-quiz/materials/`, data, {
      ...(isFormData && {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      }),
    });
    return response.data;
  },

  /**
   * Add a YouTube video as study material.
   * POST /api/ai-quiz/materials/
   * @param {Object} payload
   * @param {string} payload.youtube_url
   * @param {string} [payload.title]
   */
  addYouTubeMaterial: async ({ youtube_url, title, course_id }) => {
    const payload = { youtube_url };
    if (title && title.trim()) {
      payload.title = title.trim();
    }
    if (course_id) payload.course_id = course_id;
    const response = await axiosInstance.post(`/ai-quiz/materials/`, payload);
    return response.data;
  },

  /**
   * Deletes a study material and its parsed chunks.
   * DELETE /api/ai-quiz/materials/<id>/
   */
  deleteStudyMaterial: async (id) => {
    const response = await axiosInstance.delete(`/ai-quiz/materials/${id}/`);
    return response.data;
  },

  /**
   * Re-extracts text, re-chunks, and re-discovers topics if an upload previously failed.
   * POST /api/ai-quiz/materials/<id>/reprocess/
   */
  reprocessStudyMaterial: async (id) => {
    const response = await axiosInstance.post(`/ai-quiz/materials/${id}/reprocess/`);
    return response.data;
  },

  /**
   * Generates practice questions grounded in the uploaded material.
   * POST /api/ai-quiz/generate/
   * @param {Object} payload
   * @param {string[]} payload.material_ids
   * @param {number} [payload.question_count=10]
   * @param {string[]} [payload.topics=[]]
   * @param {string[]} [payload.question_types]
   * @param {"simple"|"normal"|"difficult"} [payload.difficulty="normal"]
   * @param {"learning"|"assessment"} [payload.quiz_mode="learning"]
   * @param {boolean} [payload.avoid_previous_questions=true]
   */
  generateAIQuiz: async (payload) => {
    const response = await axiosInstance.post(`/ai-quiz/generate/`, payload);
    return response.data;
  },

  /**
   * List User's AI Personal Courses
   * GET /ai-quiz/courses/
   */
  getAICourses: async () => {
    const response = await axiosInstance.get('/ai-quiz/courses/');
    return response.data;
  },

  /**
   * Get AI Course Detail
   * GET /ai-quiz/courses/<course_id>/
   * @param {string} courseId
   * @param {Object} [params]
   * @param {number|boolean} [params.include_topics=1]
   */
  getAICourseDetail: async (courseId, params = { include_topics: 1 }) => {
    const response = await axiosInstance.get(`/ai-quiz/courses/${courseId}/`, {
      params: {
        include_topics: params?.include_topics !== undefined ? (params.include_topics ? 1 : 0) : 1,
      },
    });
    return response.data;
  },

  /**
   * Delete an AI Course
   * DELETE /ai-quiz/courses/<course_id>/
   * @param {string} courseId
   */
  deleteAICourse: async (courseId) => {
    const response = await axiosInstance.delete(`/ai-quiz/courses/${courseId}/`);
    return response.data;
  },

  /**
   * List Previous AI Generated Quizzes (Quiz History)
   * GET /ai-quiz/quizzes/
   * @param {Object} [params]
   * @param {string} [params.course_id]
   * @param {"learning"|"assessment"} [params.quiz_type]
   */
  getAIQuizzes: async (params = {}) => {
    const cleanParams = {};
    if (params.course_id) cleanParams.course_id = params.course_id;
    if (params.quiz_type && params.quiz_type !== 'all') cleanParams.quiz_type = params.quiz_type;
    const response = await axiosInstance.get('/ai-quiz/quizzes/', {
      params: cleanParams,
    });
    return response.data;
  },
};

export default aiQuizService;
